import type { Agent, AgentStatus } from "@/types/team";
import { type ErrorType, type PageRes, apiRequest } from "../api";

export interface BotMcpTokenItem {
  id: string;
  label?: string;
  tokenPrefix: string;
  createdAt: string;
  lastUsedAt?: string;
  revoked: boolean;
}

interface AgentQueryParams {
  page?: number;
  limit?: number;
  search?: {
    s_name?: string;
    s_and_name?: string;
    s_or_name?: string;
    // Outros critérios de busca conforme documentação
  };
  teamId?: string;
  isActive?: boolean;
}

export const agentService = {
  async getAll(
    query: AgentQueryParams,
    page: number,
    limit: number
  ): Promise<[PageRes<Agent> | null, ErrorType | null]> {
    const result = await apiRequest<PageRes<Agent>>({
      loaderKey: "getAllAgents",
      method: "get",
      url: "/app/users/agents",
      params: { ...query.search, page, limit },
    });

    return result;
  },

  async getById(id: string): Promise<[Agent | null, ErrorType | null]> {
    const result = await apiRequest<Agent>({
      loaderKey: "getAgentById",
      method: "get",
      url: `/app/users/agents/${id}`,
    });

    return result;
  },

  async getResponsibles(
    query: AgentQueryParams = {},
    page?: number,
    limit?: number
  ): Promise<[PageRes<Agent> | null, ErrorType | null]> {
    return apiRequest<PageRes<Agent>>({
      loaderKey: "getResponsibles",
      method: "get",
      url: "/app/users/responsibles",
      params: { ...query.search, page, limit },
    });
  },

  async create(
    data: Partial<Agent>
  ): Promise<[Agent | null, ErrorType | null]> {
    return apiRequest<Agent>({
      loaderKey: "createAgent",
      method: "post",
      url: "/app/users/agents",
      data,
    });
  },

  async update(
    id: string,
    data: Partial<Agent>
  ): Promise<[Agent | null, ErrorType | null]> {
    return apiRequest<Agent>({
      loaderKey: "updateAgent",
      method: "put",
      url: `/app/users/agents/${id}`,
      data,
    });
  },

  async updateStatus(
    id: string,
    status: AgentStatus
  ): Promise<[Agent | null, ErrorType | null]> {
    return apiRequest<Agent>({
      loaderKey: "updateAgentStatus",
      method: "put",
      url: `/app/users/agents/${id}/status`,
      data: { status },
    });
  },

  // ─── Token MCP de um agente-bot (gerado/listado/revogado por um admin) ───────────────

  async listBotMcpTokens(agentId: string): Promise<[BotMcpTokenItem[] | null, ErrorType | null]> {
    return apiRequest<BotMcpTokenItem[]>({
      loaderKey: "listBotMcpTokens",
      method: "get",
      url: `/app/users/agents/${agentId}/mcp-tokens`,
    });
  },

  async createBotMcpToken(
    agentId: string,
    label?: string
  ): Promise<[{ id: string; token: string } | null, ErrorType | null]> {
    return apiRequest<{ id: string; token: string }>({
      loaderKey: "createBotMcpToken",
      method: "post",
      url: `/app/users/agents/${agentId}/mcp-tokens`,
      data: { label },
    });
  },

  async revokeBotMcpToken(agentId: string, tokenId: string): Promise<[null, ErrorType | null]> {
    return apiRequest<null>({
      loaderKey: "revokeBotMcpToken",
      method: "delete",
      url: `/app/users/agents/${agentId}/mcp-tokens/${tokenId}`,
    });
  },

  /** Só admin pode chamar (guardado no backend). A nova senha também é enviada por e-mail pro agente. */
  async adminChangePassword(
    userId: string,
    newPassword: string
  ): Promise<[{ msg: string } | null, ErrorType | null]> {
    return apiRequest<{ msg: string }>({
      loaderKey: "adminChangeAgentPassword",
      method: "put",
      url: "/app/users/admin/change-password",
      data: { userId, newPassword },
    });
  },
};
